/**
 * Feature flags — the release-safety valve.
 *
 * Every flag is declared here with an owner and a default, so turning something
 * off is a one-line, reviewable change instead of a code hunt. Flags resolve
 * from environment variables (NEXT_PUBLIC_* for the client, plain names on the
 * server) and fall back to the declared default when unset.
 *
 * Rules:
 *  - default-on for anything users already depend on; opt-in only for new work
 *  - flags gate EXPOSURE, never data integrity: a disabled flag hides a surface,
 *    it must never break a write path
 *  - a flag with no `killSwitch` cannot be turned off in production
 */

export type FlagKey =
  | 'engineeringPage'
  | 'changelogPage'
  | 'errorReporting'
  | 'trialLifecycleEmails';

export interface FlagDefinition {
  key: FlagKey;
  /** Env var that overrides the default. */
  env: string;
  /** Used when the env var is absent or unparseable. */
  defaultEnabled: boolean;
  /** May this flag be disabled in production? Set for reversible rollouts. */
  killSwitch: boolean;
  description: string;
}

export const FLAGS: Record<FlagKey, FlagDefinition> = {
  engineeringPage: {
    key: 'engineeringPage',
    env: 'NEXT_PUBLIC_FLAG_ENGINEERING_PAGE',
    defaultEnabled: true,
    killSwitch: true,
    description: 'Public /engineering page and its footer link',
  },
  changelogPage: {
    key: 'changelogPage',
    env: 'NEXT_PUBLIC_FLAG_CHANGELOG',
    defaultEnabled: true,
    killSwitch: true,
    description: 'Public /changelog page, RSS feed and footer link',
  },
  errorReporting: {
    key: 'errorReporting',
    env: 'NEXT_PUBLIC_FLAG_ERROR_REPORTING',
    defaultEnabled: true,
    killSwitch: true,
    description: 'Client error reports to /api/errors',
  },
  trialLifecycleEmails: {
    key: 'trialLifecycleEmails',
    env: 'FLAG_TRIAL_LIFECYCLE',
    defaultEnabled: true,
    killSwitch: true,
    description: 'Scheduled trial-expiry and win-back emails',
  },
};

function parseFlag(raw: string | undefined, defaultEnabled: boolean): boolean {
  if (raw === undefined) return defaultEnabled;
  const v = raw.trim().toLowerCase();
  if (['1', 'true', 'on', 'yes', 'enabled'].includes(v)) return true;
  if (['0', 'false', 'off', 'no', 'disabled'].includes(v)) return false;
  return defaultEnabled; // unrecognised value → declared default, never a guess
}

/** Resolve a flag on the server. */
export function isEnabled(key: FlagKey, env: NodeJS.ProcessEnv = process.env): boolean {
  const flag = FLAGS[key];
  if (!flag) return false;
  return parseFlag(env[flag.env], flag.defaultEnabled);
}

/**
 * Resolve a flag in the browser. Only NEXT_PUBLIC_* values are inlined at build
 * time, so a flag without that prefix is treated as disabled on the client
 * rather than silently reading an undefined variable.
 */
export function isEnabledClient(key: FlagKey): boolean {
  const flag = FLAGS[key];
  if (!flag || !flag.env.startsWith('NEXT_PUBLIC_')) return false;
  const raw = process.env[flag.env];
  return parseFlag(raw, flag.defaultEnabled);
}

/** Current flag state, for the engineering page / diagnostics. */
export function flagSnapshot(env: NodeJS.ProcessEnv = process.env) {
  return Object.values(FLAGS).map((f) => ({
    key: f.key,
    enabled: isEnabled(f.key, env),
    killSwitch: f.killSwitch,
    description: f.description,
  }));
}
